'use client';

import { jsx, jsxs } from "react/jsx-runtime";
import { memo, useRef } from "react";
import { Badge } from "../../atoms/Badge/Badge.js";
import { Text } from "../../atoms/Text/Text.js";
import { getTextColor } from "../Tabs/utils/getTextColor.js";
import { buttonStatus, buttonCursor, buttonVariant, button, status, tab, header, headerState, headerStick, variants, headerWrapper } from "../Tabs/Tabs.css.js";
import { CustomAnchor } from "../../quarks/CustomAnchor/CustomAnchor.js";
import { useIsStuck } from "../../utils/hooks/useIsStuck.js";
import { classNames } from "../../utils/styles/classNames.js";
import { defaultCounterLabel } from "./TabNav.data.js";
import { defaultProps } from "./TabNav.defaultProps.js";
function TabNavComponent({
  tabs,
  variant: variantProp,
  sticky,
  currentTabId,
  className,
  offsetTop: offsetTopProp,
  ...domProps
}) {
  const variant = variantProp || defaultProps.variant;
  const offsetTop = offsetTopProp || defaultProps.offsetTop;
  const isSticky = sticky;
  const headerRef = useRef(null);
  const isStuck = useIsStuck(headerRef, isSticky);
  return /* @__PURE__ */ jsx(
    "nav",
    {
      ...domProps,
      className: classNames(
        className,
        headerWrapper,
        variants[variant],
        isSticky && headerStick,
        isSticky && headerState[isStuck ? "stuck" : "default"]
      ),
      ref: headerRef,
      style: isSticky ? { top: offsetTop } : void 0,
      children: /* @__PURE__ */ jsx("ul", { className: classNames(header), children: tabs && tabs.map((tab$1) => {
        const active = !tab$1.disabled && tab$1.id === currentTabId;
        const textColor = getTextColor(active, tab$1.disabled);
        const defaultStatus = active ? "active" : "default";
        const status$1 = tab$1.disabled ? "disabled" : defaultStatus;
        const Icon = tab$1.icon;
        const iconJsx = Icon && /* @__PURE__ */ jsx(Icon, { filled: tab$1.iconStyle === "filled", size: "sm" });
        const isLink = !!tab$1.href;
        const Element = isLink ? CustomAnchor : "button";
        const counterLabel = tab$1.counter && `${tab$1.counter} ${tab$1.a11yCounterLabel ?? defaultCounterLabel(tab$1.counter)}`;
        return /* @__PURE__ */ jsx(
          "li",
          {
            className: classNames(tab, status[status$1]),
            children: /* @__PURE__ */ jsxs(
              Element,
              {
                "aria-current": active ? true : void 0,
                "aria-disabled": !isLink && tab$1.disabled ? true : void 0,
                className: classNames(
                  button,
                  !tab$1.disabled && buttonVariant[variant === "secondary" ? "secondary" : "primary"],
                  buttonCursor[tab$1.disabled ? "disabled" : "default"],
                  buttonStatus[status$1]
                ),
                href: isLink ? tab$1.href : void 0,
                role: !isLink ? "link" : void 0,
                type: !isLink ? "button" : void 0,
                onClick: tab$1.disabled ? void 0 : tab$1.onClick,
                children: [
                  iconJsx,
                  tab$1.a11yLabel && /* @__PURE__ */ jsx(Text, { visuallyHidden: true, children: tab$1.a11yLabel }),
                  /* @__PURE__ */ jsx(
                    Text,
                    {
                      "aria-hidden": tab$1.a11yLabel ? "true" : void 0,
                      as: "span",
                      color: textColor,
                      variant: "label.sm",
                      children: tab$1.label
                    }
                  ),
                  tab$1.counter && counterLabel && /* @__PURE__ */ jsx(
                    Badge,
                    {
                      a11yLabel: counterLabel,
                      emphasis: variant === "primary" ? "medium" : "high",
                      label: `${tab$1.counter}`,
                      roundness: "full",
                      variant: variant === "primary" ? "primary" : "neutral"
                    }
                  )
                ]
              }
            )
          },
          tab$1.id
        );
      }) })
    }
  );
}
const TabNav = /* @__PURE__ */ memo(TabNavComponent);
export {
  TabNav
};
